/**
 * Add / Edit vendor modal — thin config over the generic <FormModal>.
 * Create needs name + city_id. Edit allows name / status ONLY — city is set
 * once at creation and never changes (owner rule 2026-07-06), so edit mode
 * doesn't even show it and never sends it.
 */
import React, { useEffect, useState } from 'react';
import { FormModal, FieldDef } from '../../../components/manpower/FormModal';
import { useVendorMutations } from '../hooks/useVendorMutations';
import { useCities } from '../hooks/useCities';
import type { Vendor } from '../../../services/manpowerVendorsApi';

interface VendorModalProps {
	open: boolean;
	editing?: Vendor | null;
	onClose: () => void;
	onSuccess: (message: string) => void;
}

export const VendorModal: React.FC<VendorModalProps> = ({ open, editing, onClose, onSuccess }) => {
	const isEdit = !!editing;
	const { create, update } = useVendorMutations();
	const { data: cities = [] } = useCities();

	const [values, setValues] = useState<Record<string, string | number>>({});
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setValues({
				name: editing?.name ?? '',
				city_id: editing?.city_id ?? '',
				status: editing?.status ?? 'active',
			});
			setError(null);
		}
	}, [open, editing]);

	const onChange = (name: string, value: string | number) =>
		setValues(prev => ({ ...prev, [name]: value }));

	const name = String(values.name ?? '').trim();
	const nameValid = name.length >= 2 && name.length <= 150;
	const cityChosen = values.city_id !== '' && values.city_id != null;
	const canSubmit = isEdit ? nameValid : nameValid && cityChosen;
	const submitting = create.isPending || update.isPending;

	const fields: FieldDef[] = [
		{
			name: 'name',
			label: 'Name',
			type: 'text',
			required: true,
			placeholder: 'Vendor name',
			hint: !nameValid && name.length > 0 ? 'Name must be 2–150 characters.' : undefined,
		},
		// city: create-only — immutable after that, so edit hides it
		...(!isEdit
			? [
					{
						name: 'city_id',
						label: 'City',
						type: 'select' as const,
						required: true,
						placeholder: 'Select a city…',
						options: cities.map(c => ({ value: c.id, label: c.name })),
					},
				]
			: []),
		...(isEdit
			? [
					{
						name: 'status',
						label: 'Status',
						type: 'select' as const,
						options: [
							{ value: 'active', label: 'active' },
							{ value: 'inactive', label: 'inactive' },
						],
					},
				]
			: []),
	];

	const handleSubmit = async () => {
		setError(null);
		try {
			if (isEdit && editing?.id != null) {
				await update.mutateAsync({
					id: editing.id,
					// no city_id — immutable after create
					body: {
						name,
						status: values.status as Vendor['status'],
					},
				});
				onSuccess('Vendor updated');
			} else {
				await create.mutateAsync({ name, city_id: Number(values.city_id) });
				onSuccess('Vendor created');
			}
			onClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Something went wrong');
		}
	};

	return (
		<FormModal
			open={open}
			title={isEdit ? 'Edit Vendor' : 'Add Vendor'}
			fields={fields}
			values={values}
			onChange={onChange}
			canSubmit={canSubmit}
			submitting={submitting}
			submitLabel={isEdit ? 'Save changes' : 'Create vendor'}
			error={error}
			onSubmit={handleSubmit}
			onClose={onClose}
		/>
	);
};

export default VendorModal;
